import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';
import { LoadingBlock } from '../../components/Spinner';
import ScanStatsPanel from '../../components/ScanStatsPanel';
import SortableTh, { type SortDirection } from '../../components/SortableTh';

/**
 * Operations → Scans page (`/ops/scans`).
 *
 * Unified feed of "last scan" activity across:
 *   - Sources table  (RSS / iCal / Reddit / Email / API pollers)
 *   - Venues         (per-venue Instagram / Facebook / Website)
 *   - EventSeries    (per-series Instagram / Facebook / Website)
 *
 * Backed by `GET /api/mgmt/scans/recent`. Polls every 30s while the tab is
 * visible.
 *
 * Accessibility:
 * - Single `<h1>` (page heading).
 * - Filter `<select>`s have `<label htmlFor>` associations.
 * - Status badges include a text label (not color-only) for screen readers.
 */

type ScanChannel =
  | 'instagram'
  | 'facebook'
  | 'website'
  | 'reddit'
  | 'rss'
  | 'ical'
  | 'email'
  | 'eventbrite'
  | 'meetup'
  | 'bandsintown'
  | 'api'
  | 'manual'
  | 'venue';

type ScanStatus = 'success' | 'error' | 'skipped';

interface ScanRow {
  channel: ScanChannel;
  targetKind: 'source' | 'venue' | 'series';
  targetId: string;
  targetName: string;
  targetPartitionKey?: string;
  scannedAt: string;
  status: ScanStatus;
  count?: number;
  error?: string;
  nextEligibleAt?: string;
}

interface ScansResponse {
  items: ScanRow[];
  total: number;
  limit: number;
  channelCounts: Record<string, number>;
  statusCounts: Record<ScanStatus, number>;
}

const CHANNELS: { value: '' | ScanChannel; label: string }[] = [
  { value: '', label: 'All channels' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'website', label: 'Website' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'rss', label: 'RSS' },
  { value: 'ical', label: 'iCal' },
  { value: 'email', label: 'Email' },
  { value: 'eventbrite', label: 'Eventbrite' },
  { value: 'meetup', label: 'Meetup' },
  { value: 'bandsintown', label: 'Bandsintown' },
  { value: 'api', label: 'API' },
  { value: 'manual', label: 'Manual' },
  { value: 'venue', label: 'Venue feed' }
];

const STATUSES: { value: '' | ScanStatus; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'success', label: 'Success' },
  { value: 'error', label: 'Error' },
  { value: 'skipped', label: 'Skipped' }
];

type ScanTargetKind = 'source' | 'venue' | 'series';

const TARGET_KINDS: { value: '' | ScanTargetKind; label: string }[] = [
  { value: '', label: 'All targets' },
  { value: 'venue', label: 'Venues' },
  { value: 'series', label: 'Series' },
  { value: 'source', label: 'Sources' }
];

// Sortable columns for the recent-scans table + the default direction applied
// when a column first becomes active (time/number = desc, text = asc).
type RecentSortKey = 'when' | 'channel' | 'target' | 'status' | 'new' | 'next';
const RECENT_DEFAULT_DIR: Record<RecentSortKey, SortDirection> = {
  when: 'desc',
  channel: 'asc',
  target: 'asc',
  status: 'asc',
  new: 'desc',
  next: 'asc'
};

const STATUS_BADGE: Record<ScanStatus, string> = {
  success: 'mh-scan-badge--success',
  error: 'mh-scan-badge--error',
  skipped: 'mh-scan-badge--skipped'
};

const STATUS_ICON: Record<ScanStatus, string> = {
  success: 'check-circle-fill',
  error: 'exclamation-triangle-fill',
  skipped: 'skip-forward-fill'
};

const CHANNEL_ICON: Partial<Record<ScanChannel, string>> = {
  instagram: 'instagram',
  facebook: 'facebook',
  website: 'globe',
  reddit: 'reddit',
  rss: 'rss',
  ical: 'calendar-event',
  email: 'envelope',
  eventbrite: 'ticket-perforated',
  meetup: 'people',
  bandsintown: 'music-note-beamed',
  api: 'hdd-network',
  manual: 'hand-index',
  venue: 'geo-alt'
};

function ChannelPill({ channel }: { channel: ScanChannel }) {
  const icon = CHANNEL_ICON[channel] ?? 'broadcast';
  return (
    <span className={`mh-channel-pill mh-channel-pill--${channel}`}>
      <span className="mh-channel-pill__dot" aria-hidden="true" />
      <i className={`bi bi-${icon}`} aria-hidden="true" />
      {channel}
    </span>
  );
}

const POLL_INTERVAL_MS = 30_000;

function formatRelative(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
}

function formatAbsolute(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatNextEligible(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diffMs = d.getTime() - Date.now();
  if (diffMs <= 0) return 'eligible now';
  const min = Math.round(diffMs / 60_000);
  if (min < 60) return `in ${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `in ${hr}h`;
  const day = Math.round(hr / 24);
  return `in ${day}d`;
}

function targetLink(row: ScanRow): ReactElement {
  if (row.targetKind === 'venue') {
    return (
      <Link to={`/venues/${row.targetId}`} className="text-decoration-none">
        {row.targetName}
      </Link>
    );
  }
  if (row.targetKind === 'series') {
    return (
      <Link to={`/series/${row.targetId}`} className="text-decoration-none">
        {row.targetName}
      </Link>
    );
  }
  // Source rows aren't individually navigable; tag with the kind for clarity.
  return <span>{row.targetName}</span>;
}

/**
 * Map a scan row to the per-target rerun endpoint that already powers the
 * "Force scan" buttons on the venue / series detail pages. Returns null when
 * no per-target endpoint exists yet (singleton-style sources like email,
 * eventbrite, and bandsintown have no per-row state — those still return null).
 */
const PER_ROW_SOURCE_TYPES = new Set(['rss', 'ical', 'reddit']);

function rerunEndpoint(row: ScanRow): string | null {
  const id = encodeURIComponent(row.targetId);
  if (row.targetKind === 'venue') {
    if (row.channel === 'instagram') return `/api/mgmt/venues/${id}/scan`;
    if (row.channel === 'facebook') return `/api/mgmt/venues/${id}/scan-facebook`;
    if (row.channel === 'website') return `/api/mgmt/venues/${id}/scan-website`;
  }
  if (row.targetKind === 'series') {
    if (row.channel === 'instagram') return `/api/mgmt/series/${id}/scan`;
    if (row.channel === 'facebook') return `/api/mgmt/series/${id}/scan-facebook`;
    if (row.channel === 'website') return `/api/mgmt/series/${id}/scan-website`;
  }
  if (row.targetKind === 'source') {
    const pk = row.targetPartitionKey || row.channel;
    if (PER_ROW_SOURCE_TYPES.has(pk)) {
      return `/api/mgmt/sources/${encodeURIComponent(pk)}/${id}/scan`;
    }
  }
  return null;
}

function rerunRowKey(row: ScanRow): string {
  return `${row.targetKind}:${row.targetId}:${row.channel}`;
}

/** Epoch millis for an ISO string; missing/invalid sorts last in ascending order. */
function sortableTime(iso: string | undefined): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/** Numeric compare that returns 0 for equal values (incl. Infinity === Infinity)
 *  so the caller's stable index tiebreak can run instead of yielding NaN. */
function cmpNum(x: number, y: number): number {
  return x === y ? 0 : x < y ? -1 : 1;
}

function compareRecent(a: ScanRow, b: ScanRow, key: RecentSortKey): number {
  switch (key) {
    case 'when':
      return cmpNum(sortableTime(a.scannedAt), sortableTime(b.scannedAt));
    case 'channel':
      return a.channel.localeCompare(b.channel);
    case 'target':
      return (a.targetName || '').localeCompare(b.targetName || '');
    case 'status':
      return a.status.localeCompare(b.status);
    case 'new':
      return cmpNum(a.count ?? -1, b.count ?? -1);
    case 'next':
      return cmpNum(sortableTime(a.nextEligibleAt), sortableTime(b.nextEligibleAt));
  }
}

export default function AdminScansPage() {
  const [data, setData] = useState<ScansResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [channel, setChannel] = useState<'' | ScanChannel>('');
  const [status, setStatus] = useState<'' | ScanStatus>('');
  const [targetKind, setTargetKind] = useState<'' | ScanTargetKind>('');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(200);
  const [sortKey, setSortKey] = useState<RecentSortKey>('when');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [rerunning, setRerunning] = useState<string | null>(null);
  const [rerunMsg, setRerunMsg] = useState<{ key: string; text: string; ok: boolean } | null>(null);

  const load = useMemo(
    () => () => {
      const params = new URLSearchParams();
      if (channel) params.set('channel', channel);
      if (status) params.set('status', status);
      params.set('limit', String(limit));
      setErr(null);
      api
        .get<ScansResponse>(`/api/mgmt/scans/recent?${params.toString()}`)
        .then(setData)
        .catch((e) => setErr(String(e?.message ?? e)))
        .finally(() => setLoading(false));
    },
    [channel, status, limit]
  );

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Background poll. Don't refetch when tab is hidden — saves quota.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  /**
   * Rerun a single scan by POSTing to the same per-target endpoint that the
   * "Force scan" buttons on the venue/series detail pages already use. After
   * the call returns we refresh the feed so the new `lastScanAt` row appears
   * (or the new error message replaces the stale one).
   */
  const rerun = async (row: ScanRow) => {
    const url = rerunEndpoint(row);
    if (!url) return;
    const key = rerunRowKey(row);
    setRerunning(key);
    setRerunMsg(null);
    try {
      const res = await api.post<{ ingested?: number; issues?: string[] }>(url);
      const ingested = typeof res?.ingested === 'number' ? res.ingested : 0;
      const issues = Array.isArray(res?.issues) ? res!.issues!.filter(Boolean) : [];
      const text = issues.length
        ? `Rerun finished with issue: ${issues[0]}`
        : `Rerun finished — ${ingested.toLocaleString()} new item${ingested === 1 ? '' : 's'}.`;
      setRerunMsg({ key, text, ok: issues.length === 0 });
      load();
    } catch (e: any) {
      setRerunMsg({ key, text: `Rerun failed: ${String(e?.message ?? e)}`, ok: false });
    } finally {
      setRerunning(null);
    }
  };

  // Clicking a header toggles direction when it's already active, otherwise
  // switches to that column with its sensible default direction.
  const toggleSort = (key: string) => {
    const k = key as RecentSortKey;
    if (k === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir(RECENT_DEFAULT_DIR[k]);
    }
  };

  // Target-type + free-text filters are applied client-side (the API only
  // understands channel/status/limit), then the active column sort is applied.
  const visibleItems = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    const filtered = data.items.filter((row) => {
      if (targetKind && row.targetKind !== targetKind) return false;
      if (needle && !(row.targetName || '').toLowerCase().includes(needle)) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return filtered
      .map((row, idx) => ({ row, idx }))
      .sort((a, b) => {
        const cmp = compareRecent(a.row, b.row, sortKey);
        return cmp !== 0 ? cmp * dir : a.idx - b.idx; // stable tiebreak
      })
      .map((x) => x.row);
  }, [data, targetKind, search, sortKey, sortDir]);

  return (
    <AdminLayout>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h1 className="mb-0">Scan history</h1>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <p className="mh-scan-intro small">
        Latest scan activity across venues, series and source feeds — Instagram, Facebook, websites,
        Reddit, RSS, iCal, email and other pollers. Auto-refreshes every 30 seconds.
      </p>

      <ScanStatsPanel />

      <h2 className="h5 mt-4 mb-2">Recent scans</h2>

      {data && (
        <div className="mh-scan-tiles mb-3" aria-label="Scan summary counts — select to filter by status">
          {(['success', 'skipped', 'error'] as const).map((s) => {
            const labels: Record<typeof s, string> = { success: 'Success', skipped: 'Skipped', error: 'Error' } as Record<typeof s, string>;
            const active = status === s;
            return (
              <button
                key={s}
                type="button"
                className={`mh-scan-tile mh-scan-tile--${s}${active ? ' is-active' : ''}`}
                aria-pressed={active}
                onClick={() => setStatus(active ? '' : s)}
              >
                <span className="mh-scan-tile__label">
                  <i className={`bi bi-${STATUS_ICON[s]}`} aria-hidden="true" />
                  {labels[s]}
                </span>
                <span className="mh-scan-tile__value">{data.statusCounts[s].toLocaleString()}</span>
              </button>
            );
          })}
        </div>
      )}

      <form
        className="mh-scan-toolbar row g-2 align-items-end mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
      >
        <div className="col-sm-6 col-lg-3">
          <label htmlFor="scan-channel" className="form-label">Channel</label>
          <select
            id="scan-channel"
            className="form-select"
            value={channel}
            onChange={(e) => setChannel(e.target.value as '' | ScanChannel)}
          >
            {CHANNELS.map((c) => (
              <option key={c.value || 'all-channels'} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-6 col-lg-3">
          <label htmlFor="scan-status" className="form-label">Status</label>
          <select
            id="scan-status"
            className="form-select"
            value={status}
            onChange={(e) => setStatus(e.target.value as '' | ScanStatus)}
          >
            {STATUSES.map((s) => (
              <option key={s.value || 'all-statuses'} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-6 col-lg-2">
          <label htmlFor="scan-target-kind" className="form-label">Target type</label>
          <select
            id="scan-target-kind"
            className="form-select"
            value={targetKind}
            onChange={(e) => setTargetKind(e.target.value as '' | ScanTargetKind)}
          >
            {TARGET_KINDS.map((t) => (
              <option key={t.value || 'all-targets'} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-6 col-lg-2">
          <label htmlFor="scan-search" className="form-label">Search target</label>
          <input
            id="scan-search"
            type="search"
            className="form-control"
            placeholder="Name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="col-sm-6 col-lg-2">
          <label htmlFor="scan-limit" className="form-label">Limit</label>
          <select
            id="scan-limit"
            className="form-select"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            {[50, 100, 200, 500].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </form>

      {err && (
        <div className="alert alert-danger" role="alert">
          {err}
        </div>
      )}

      {loading && !data && <LoadingBlock label="Loading scans…" />}

      {data && (
        <>
          <p className="small text-body-secondary mb-2">
            Showing {visibleItems.length.toLocaleString()} of {data.total.toLocaleString()} matching scans
            {(targetKind || search.trim()) ? ' (filtered)' : ''}.
          </p>
          <div className="mh-scan-table-wrap">
            <table className="table table-sm align-middle mh-scan-table">
              <thead>
                <tr>
                  <SortableTh label="When" sortKey="when" activeKey={sortKey} direction={sortDir} onSort={toggleSort} />
                  <SortableTh label="Channel" sortKey="channel" activeKey={sortKey} direction={sortDir} onSort={toggleSort} />
                  <SortableTh label="Target" sortKey="target" activeKey={sortKey} direction={sortDir} onSort={toggleSort} />
                  <SortableTh label="Status" sortKey="status" activeKey={sortKey} direction={sortDir} onSort={toggleSort} />
                  <SortableTh label="New" sortKey="new" activeKey={sortKey} direction={sortDir} onSort={toggleSort} align="end" />
                  <th scope="col">Detail</th>
                  <SortableTh label="Next" sortKey="next" activeKey={sortKey} direction={sortDir} onSort={toggleSort} />
                  <th scope="col" className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.length === 0 && (
                  <tr>
                    <td colSpan={8}>
                      <div className="mh-scan-empty">
                        <i className="bi bi-search" aria-hidden="true" />
                        <p className="mb-0 small">No scan activity matches the current filters.</p>
                      </div>
                    </td>
                  </tr>
                )}
                {visibleItems.map((row) => {
                  const key = rerunRowKey(row);
                  const endpoint = rerunEndpoint(row);
                  const isRerunning = rerunning === key;
                  const msg = rerunMsg?.key === key ? rerunMsg : null;
                  return (
                  <tr key={key}>
                    <td className="small" title={formatAbsolute(row.scannedAt)}>
                      {formatRelative(row.scannedAt)}
                    </td>
                    <td>
                      <ChannelPill channel={row.channel} />
                    </td>
                    <td>
                      {targetLink(row)}
                      <span className="small text-body-secondary ms-2">({row.targetKind})</span>
                    </td>
                    <td>
                      <span className={`mh-scan-badge ${STATUS_BADGE[row.status]}`}>
                        <i className={`bi bi-${STATUS_ICON[row.status]}`} aria-hidden="true" />
                        {row.status}
                      </span>
                    </td>
                    <td className="text-end small">
                      {typeof row.count === 'number' ? row.count.toLocaleString() : '—'}
                    </td>
                    <td className="small">
                      {row.error ? (
                        <span className={row.status === 'error' ? 'text-danger' : 'text-body-secondary'}>
                          {row.error}
                        </span>
                      ) : (
                        <span className="text-body-secondary">—</span>
                      )}
                    </td>
                    <td className="small text-body-secondary" title={formatAbsolute(row.nextEligibleAt || '')}>
                      {formatNextEligible(row.nextEligibleAt)}
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => rerun(row)}
                        disabled={!endpoint || isRerunning}
                        title={endpoint
                          ? 'Re-run this scan now (ignores cooldown).'
                          : 'Singleton-style sources (email, eventbrite, bandsintown) have no per-row state — driven by a fixed-cadence timer poll instead.'}
                        aria-label={`Rerun ${row.channel} scan for ${row.targetName}`}
                      >
                        {isRerunning ? 'Rerunning…' : 'Rerun'}
                      </button>
                      {msg && (
                        <div className={`small mt-1 ${msg.ok ? 'text-success' : 'text-danger'}`}>
                          {msg.text}
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
